namespace advisorop.services {
    `use strict`;

    /**
     * We will use this service for data and functionalities 
     * sharing between different dashboards for advisor
     * 
     * THINK ABOUT: maybe it would be better to have different services for each dashboard
     */
    export class AdvisorDashboardService {
        static $inject = [`$http`, `householdApi`, `backOfficeLinkApi`];
        constructor(private $http: ng.IHttpService,
            private hhApi: services.HouseholdApi,
            private backOfficeLinkApi: services.BackOfficeLinkApi) {
        }

        /**
         * Pointers to opened browser windows with PP screens
         */
        private ppWnds = {};
        
        /**
        * Empty chart data for Asset Allocation component/chart
        */
        getEmptyChartData = (): Array<domains.IChartDataSource> => {
            return [{
                chart: {
                    caption: "...", numberPrefix: "", subCaption: "", theme: ""
                },
                data: [],
                categories: [],
                dataset: []
            }];
        };

        /** 
         *  Call proper getHDCharts API based on provided "level"
         */
        getHdChartPromise = (level: string, householdId: number, groupType: number, groupId: number, accountId: number,
            sleeveId: number, showClosedInactive?: boolean, loadHistData: boolean = false, histChartDateType: domains.HistChartDateType = domains.HistChartDateType.LAST_SIX_MONTHS): ng.IHttpPromise<domains.IChartsDataSource> => {

            if (level == "H") {
                return this.hhApi.getCharts(householdId, groupType, level, null, null, null, showClosedInactive, null, loadHistData, histChartDateType);
            } else if (level == "R") {
                return this.hhApi.getCharts(householdId, groupType, level, groupId, null, null, showClosedInactive, null, loadHistData, histChartDateType);
            } else if (level == "A") {
                return this.hhApi.getCharts(householdId, groupType, level, groupId, accountId, null, showClosedInactive, null, loadHistData, histChartDateType);
            } else if (level == "S") {
                return this.hhApi.getCharts(householdId, groupType, level, groupId, accountId, sleeveId, showClosedInactive, null, loadHistData, histChartDateType);
            }

            return null;
        };

        getHdChartXlsx = (level: string, householdId: number, groupType: number, groupId: number, accountId: number,
            sleeveId: number, showClosedInactive?: boolean): ng.IHttpPromise<any> => {

            if (level == "H") {
                return this.hhApi.getChartsXlsx(householdId, groupType, level, null, null, null, showClosedInactive);
            } else if (level == "R") {
                return this.hhApi.getChartsXlsx(householdId, groupType, level, groupId, null, null, showClosedInactive);
            } else if (level == "A") {
                return this.hhApi.getChartsXlsx(householdId, groupType, level, groupId, accountId, null, showClosedInactive);
            } else if (level == "S") {
                return this.hhApi.getChartsXlsx(householdId, groupType, level, groupId, accountId, sleeveId, showClosedInactive);
            }

            return null;
        };

        /**
         * Calculate Level and ClientId/EntityId for PortfolioPathway
         */
        calculatePpLevelAndClientId = (level: string, boRtgId: string, boCustodialAccountNumber: string,
            boUMAAccountIdentifierString: string): { pplevel: string, ppclientid: string } => {

            let noData = false;

            const ppData = {
                pplevel: null,
                ppclientid: null
            };

            if (level == "H") {
                // WE DONT SYNC OUR HH WITH PP
                noData = true;
            } else if (level == "R") {
                // AdvisorOp RTG level is PP household level
                if (boRtgId) {
                    ppData.pplevel = "HH";
                    ppData.ppclientid = boRtgId;
                } else {
                    noData = true;
                }
            }
            else if (level == "A") {
                // AdvisorOp account level can be PP account or PP client group level
                // Checking first char of boUMAAccountIdentifierString is a hack to identify Folio accounts
                // We need to provide custodian from the server query and check that here instead.
                if (boUMAAccountIdentifierString && boUMAAccountIdentifierString.charAt(0) != 'R') {
                    ppData.pplevel = "CG";
                    ppData.ppclientid = "U-" + boUMAAccountIdentifierString;
                } else if (boCustodialAccountNumber) {
                    ppData.pplevel = "AC";
                    ppData.ppclientid = boCustodialAccountNumber;
                } else {
                    noData = true;
                }
            }
            else if (level == "S") {
                // AdvisorOp sleeve level is PP account level
                if (boCustodialAccountNumber) {
                    ppData.pplevel = "AC";
                    ppData.ppclientid = boCustodialAccountNumber;
                } else {
                    noData = true;
                }
            }

            if (noData) {
                // toastr.warning("There is no available back office data for this selection.");
                return null;
            }

            return ppData;
        }

        calculatePpParams = (
            level: string,
            boRtgId: string,
            boCustodialAccountNumber: string,
            boUMAAccountIdentifierString: string): { backOfficeLevel: string, backOfficeEntityId: string } => {

            let noData = false;

            const backOfficeParams = {
                backOfficeLevel: null,
                backOfficeEntityId: null
            };

            if (level == "H") {
                // WE DONT SYNC OUR HH WITH PP
                noData = true;
            } else if (level == "R") {
                // AdvisorOp RTG level is PP household level
                if (boRtgId) {
                    backOfficeParams.backOfficeLevel = "HH";
                    backOfficeParams.backOfficeEntityId = boRtgId;
                } else {
                    noData = true;
                }
            }
            else if (level == "A") {
                // AdvisorOp account level can be PP account or PP client group level
                // Checking first char of boUMAAccountIdentifierString is a hack to identify Folio accounts
                // We need to provide custodian from the server query and check that here instead.
                if (boUMAAccountIdentifierString && boUMAAccountIdentifierString.charAt(0) != 'R') {
                    backOfficeParams.backOfficeLevel = "CG";
                    backOfficeParams.backOfficeEntityId = "U-" + boUMAAccountIdentifierString;
                } else if (boCustodialAccountNumber) {
                    backOfficeParams.backOfficeLevel = "AC";
                    backOfficeParams.backOfficeEntityId = boCustodialAccountNumber;
                } else {
                    noData = true;
                }
            }
            else if (level == "S") {
                // AdvisorOp sleeve level is PP account level
                if (boCustodialAccountNumber) {
                    backOfficeParams.backOfficeLevel = "AC";
                    backOfficeParams.backOfficeEntityId = boCustodialAccountNumber;
                } else {
                    noData = true;
                }
            }

            if (noData) {
                // toastr.warning("There is no available back office data for this selection.");
                return null;
            }

            return backOfficeParams;
        }

        /**
         * Calculates back office parameters for Orion
         * @param level AdvisorOp level which can be H-household,R-portfolio group,A-account,S-sleeve account
         * @param entityId 
         * @param boCustodialAccountNumber
         * @param boUMAAccountIdentifierString
         * @returns Entity and EntityId for Orion, Entity can take 5 - Household, 7 - Account, 107 - Portfolio Group (RTG level)
         */
        calculateOrionParams = (
            level: string,
            entityId: string): domains.IBackOfficeOrionParams => {

            // it must be AdvisorOp level and AdvisorOp EntityId
            if (!level || !entityId) {
                return null;
            }

            let backOfficeParams: domains.IBackOfficeOrionParams;

            this.backOfficeLinkApi.calculateOrionParams(level, parseInt(entityId))
                .then((data) => {
                    console.log(`We've got orion params:`, data.data);
                    backOfficeParams = data.data;
                })
                .catch((err) => {
                    console.error(`Error calculating Orion back office params for level: ${level}, entityId: ${entityId}`, err);
                    throw err;
                });
            
            return backOfficeParams;
        }


        calculateOrionParamsAsync = (level: string, entityId: string): angular.IPromise<domains.IBackOfficeParams> => {
            // it must be AdvisorOp level and AdvisorOp EntityId
            if (!level || !entityId) {
                return null;
            }

            let backOfficeParams: domains.IBackOfficeParams;

            return this.backOfficeLinkApi.calculateOrionParams(level, parseInt(entityId))
                .then((response) => {
                    console.log(`We've got orion params:`, response.data);
                    const data = response.data as domains.IBackOfficeOrionParams;
                    const mappedParams: domains.IBackOfficeParams = {
                        backOfficeLevel: data.backOfficeLevel,
                        backOfficeEntityId: data.backOfficeEntityId?.toString()
                    };
                    return mappedParams;
                })
                .catch((err) => {
                    console.error(`Error calculating Orion back office params for level: ${level}, entityId: ${entityId}`, err);
                    throw err;
                });
        }

        /**
         * Calculates back office parameters for both Orion and PortfolioPathway
         * @param level AdvisorOp level which can be H-household,R-portfolio group,A-account,S-sleeve account
         * @param entityId 
         * @param boCustodialAccountNumber
         * @param boUMAAccountIdentifierString
         * @returns Entity and EntityId for BackOffice
         */
        calculateBackOfficeParams = (
            level: string,
            entityId: string): { backOfficeLevel: string, backOfficeEntityId: string } => {

            // currently we want to support only Orion, PP is a legacy system
            var params = this.calculateOrionParams(level, entityId);

            return { backOfficeLevel: params.backOfficeLevel,  backOfficeEntityId: `${params.backOfficeEntityId}` };
        }

        /**
         * Calculates back office parameters for both Orion and PortfolioPathway
         * @param level AdvisorOp level which can be H-household,R-portfolio group,A-account,S-sleeve account
         * @param entityId 
         * @returns IBackOfficeParams
         */
        calculateBackOfficeParamsAsync = (level: string, entityId: string): angular.IPromise<domains.IBackOfficeParams> => {

            // currently we want to support only Orion, PP is a legacy system
            return this.calculateOrionParamsAsync(level, entityId);
        }

        /**
         * Opens report in the BackOffice system inside a new browser window
         */
        openBackOffice = (report: string, ppParams: { backOfficeLevel, backOfficeEntityId }): void => {
            this.openOrionReport(report, ppParams);
        }

        openBackOfficeReport = (report: string, params: domains.IBackOfficeParams): void => {
            console.log(`Opening back office report ${report}`, params);
            this.openOrionReport(report, params);
        }

        private openPPReport = (report: string, ppParams: { backOfficeLevel, backOfficeEntityId }): void => {

            if (ppParams == null || !report) {
                //console.log(`PP report cannot be called. There is no report or param data. report: ${report}, params:`, advOpParams);
                return;
            }

            let url = `/samlidp/openppreport/${ppParams.backOfficeLevel}/${ppParams.backOfficeEntityId}/${report}`;
            if (!this.ppWnds[ppParams.backOfficeEntityId] || (<Window>this.ppWnds[ppParams.backOfficeEntityId]).closed) {
                //console.log("Opening report in new window: " + report);
                this.ppWnds[ppParams.backOfficeEntityId] = window.open(url, "_blank");
                if (!this.ppWnds[ppParams.backOfficeEntityId]) {
                    toastr.warning("Your browser is blocking popups from this site. Please enable popups for AdvisorOp.");
                }
            } else {
                //console.log("Opening report in existing window: " + report);
                this.ppWnds[ppParams.backOfficeEntityId].location.href = url;
                this.ppWnds[ppParams.backOfficeEntityId].focus();
            }
        }

        private openOrionReport = (report: string, params: domains.IBackOfficeParams): void => {
            console.log(`Report: ${report} Level ${params.backOfficeLevel}, ClientId ${params.backOfficeEntityId}`);

            let url = `https://api.cloud.orionadvisor.com/orionconnectapp/#/orion/dashboard`;
            if (report === 'OrionDashboard') {
                url = `https://api.cloud.orionadvisor.com/orionconnectapp/#/orion/dashboard?entity=${params.backOfficeLevel}&entityId=${params.backOfficeEntityId}`;
            }
            console.log('Url: ', url);


            if (!this.ppWnds[0] || (<Window>this.ppWnds[0]).closed) {
                //console.log("Opening report in new window: " + report);
                this.ppWnds[0] = window.open(url, "_blank");
                if (!this.ppWnds[0]) {
                    toastr.warning("Your browser is blocking popups from this site. Please enable popups for AdvisorOp.");
                }
            } else {
                //console.log("Opening report in existing window: " + report);
                this.ppWnds[0].location.href = url;
                this.ppWnds[0].focus();
            }
        }
    }
    angular.module(Constants.ADVOP_SERVICES).service(`advisorDashboardService`, AdvisorDashboardService);
}